import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import isMobile from "is-mobile";
import { useRef, useState, useContext, createContext, useEffect } from "react";
import { useFallbackId } from "../../hooks/useFallbackId.mjs";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect.mjs";
import { Box } from "../Box/Box.mjs";
import { Popover } from "../private/Popover/Popover.mjs";
import { animationTimeout } from "../private/animationTimeout.mjs";
import { DefaultTextPropsProvider } from "../private/defaultTextProps.mjs";
import { useSpace } from "../useSpace/useSpace.mjs";
import { useThemeName } from "../useThemeName/useThemeName.mjs";
import { maxWidth, translateZ0, overflowWrap, arrow, horizontalOffset } from "./TooltipRenderer.css.mjs";
const edgeOffset = "xxsmall";
const offsetSpace = "small";
const StaticTooltipContext = createContext(false);
const TooltipTextDefaultsProvider = ({
  children
}) => {
  const themeName = useThemeName();
  return /* @__PURE__ */ jsx(
    DefaultTextPropsProvider,
    {
      size: themeName === "docs" ? "small" : void 0,
      weight: "medium",
      children
    }
  );
};
const TooltipContent = ({
  inferredPlacement,
  arrowLeftOffset,
  children
}) => /* @__PURE__ */ jsx(
  Box,
  {
    textAlign: "left",
    boxShadow: "large",
    background: "neutral",
    borderRadius: "large",
    padding: "small",
    marginX: edgeOffset,
    className: [maxWidth, translateZ0],
    children: /* @__PURE__ */ jsxs(TooltipTextDefaultsProvider, { children: [
      /* @__PURE__ */ jsx(Box, { className: overflowWrap, zIndex: 1, position: "relative", children }),
      /* @__PURE__ */ jsx(
        Box,
        {
          position: "fixed",
          background: "neutral",
          className: arrow[inferredPlacement],
          style: assignInlineVars({
            [horizontalOffset]: `${arrowLeftOffset}px`
          })
        }
      )
    ] })
  }
);
const TooltipRenderer = ({
  id,
  tooltip,
  placement = "top",
  children
}) => {
  const resolvedId = useFallbackId(id);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [triggerPosition, setTriggerPosition] = useState(
    void 0
  );
  const [tooltipPosition, setTooltipPosition] = useState(
    void 0
  );
  const { grid, space } = useSpace();
  const isStatic = useContext(StaticTooltipContext);
  const isMobileDevice = useRef(isMobile()).current;
  const onScreen = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        onScreen.current = entry.isIntersecting;
      }
    );
    if (triggerRef.current) {
      observer.observe(triggerRef.current);
    }
    const handleScroll = () => {
      if (onScreen.current || onScreen.current === null) {
        setOpen(false);
      }
    };
    const scrollHandlerOptions = {
      capture: true,
      passive: true
    };
    document.addEventListener("scroll", handleScroll, scrollHandlerOptions);
    return () => {
      observer.disconnect();
      document.removeEventListener(
        "scroll",
        handleScroll,
        scrollHandlerOptions
      );
    };
  }, []);
  useEffect(() => {
    const toggleTooltip = () => setOpen(!open);
    const openTooltip = () => setOpen(true);
    const closeTooltip = () => setOpen(false);
    if (triggerRef.current) {
      triggerRef.current.addEventListener("blur", closeTooltip);
      if (isMobileDevice) {
        triggerRef.current.addEventListener("click", toggleTooltip);
      } else {
        triggerRef.current.addEventListener("focus", openTooltip);
        triggerRef.current.addEventListener("mouseenter", openTooltip);
        triggerRef.current.addEventListener("mouseleave", closeTooltip);
      }
    }
    return () => {
      if (triggerRef.current) {
        triggerRef.current.removeEventListener("blur", closeTooltip);
        if (isMobileDevice) {
          triggerRef.current.removeEventListener("click", toggleTooltip);
        } else {
          triggerRef.current.removeEventListener("focus", openTooltip);
          triggerRef.current.removeEventListener("mouseenter", openTooltip);
          triggerRef.current.removeEventListener("mouseleave", closeTooltip);
        }
      }
    };
  }, [open, isMobileDevice]);
  useIsomorphicLayoutEffect(() => {
    const setPositions = () => {
      var _a, _b;
      setTooltipPosition((_a = tooltipRef.current) == null ? void 0 : _a.getBoundingClientRect());
      setTriggerPosition((_b = triggerRef.current) == null ? void 0 : _b.getBoundingClientRect());
    };
    const timeoutId = setTimeout(() => {
      const frameId = requestAnimationFrame(setPositions);
      return () => cancelAnimationFrame(frameId);
    }, animationTimeout / 2);
    return () => clearTimeout(timeoutId);
  });
  let inferredPlacement = placement;
  let arrowLeftOffset = 0;
  if (tooltipPosition && triggerPosition) {
    inferredPlacement = tooltipPosition.top > triggerPosition.top ? "bottom" : "top";
    const edgeOffsetInPx = space[edgeOffset] * grid;
    const tooltipLeftToTriggerLeft = triggerPosition.left - tooltipPosition.left - edgeOffsetInPx;
    arrowLeftOffset = tooltipLeftToTriggerLeft + triggerPosition.width / 2;
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    children({
      triggerProps: {
        tabIndex: 0,
        ref: (el) => {
          triggerRef.current = el;
        },
        "aria-describedby": resolvedId
      }
    }),
    /* @__PURE__ */ jsx(
      Popover,
      {
        id: resolvedId,
        role: "tooltip",
        ref: tooltipRef,
        offsetSpace,
        align: "center",
        placement,
        lockPlacement: isStatic,
        delayVisibility: !isMobileDevice,
        modal: false,
        open: isStatic ? true : open,
        onClose: !isStatic ? () => setOpen(false) : void 0,
        triggerRef,
        children: /* @__PURE__ */ jsx(
          TooltipContent,
          {
            inferredPlacement,
            arrowLeftOffset,
            children: tooltip
          }
        )
      }
    )
  ] });
};
export {
  TooltipRenderer
};
